'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Collapse, Alert, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DataTable from '../../components/DataTable';
import ActionMenu from '../../components/ActionMenu';
import { useNotifier } from '../../components/Notifier';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';

import { useAppSettingDataList, deleteAppSetting } from '../../stores/appSettingStore';
import AppSettingAddEdit from './AppSettingAddEdit';
import { useLanguage } from '../../utils/i18n';
import { fleetApi } from '../../api/axiosClient';
import { useCompanyList } from '../../stores/companyStore';

function AppSettingPageContent() {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  const { companyOptions } = useCompanyList();

  // --- 1. SEARCH STATES ---
  const [searchField, setSearchField] = useState('web_title');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState(''); // Debounced value

  // STATE LOKAL UNTUK PENCARIAN 'ALL'
  const [localSettings, setLocalSettings] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);
  const [lastNotifiedSearch, setLastNotifiedSearch] = useState('');
  const [showFallbackWarning, setShowFallbackWarning] = useState(false);

  const EMPTY_ARRAY = useMemo(() => [], []);

  const getCompanyLabel = useCallback(
    (row) => {
      if (!row) return 'Default';
      if (row.join?.company_name) return row.join.company_name;
      const comp = companyOptions.find((c) => String(c.value) === String(row.company));
      return comp ? comp.label : row.company || 'Default';
    },
    [companyOptions]
  );

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'company', table_name: 'm_company', label: 'Company' },
      { value: 'web_title', label: t('appSetting.webTitle', 'Web Title') },
      { value: 'login_title', label: t('appSetting.loginTitle', 'Login Title') },
      { value: 'header_color', label: t('appSetting.headerTitle', 'Header Color') },
      { value: 'labels_customer_point', label: t('appSetting.jettyLabel', 'Jetty Label') },
      { value: 'labels_pool', label: t('appSetting.pitLabel', 'PIT Label') },
      { value: 'labels_geofence', label: t('appSetting.geofenceLabel', 'Geofence Label') }
    ],
    [t]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [searchRaw, searchField]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchValue;

  const {
    data: settingsFromHook,
    totalRecord: hookTotal,
    isLoading: hookLoading
  } = useAppSettingDataList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue);

  const hookRows = settingsFromHook?.length ? settingsFromHook : EMPTY_ARRAY;
  const rows = searchField === 'all' ? (localSettings?.length ? localSettings : EMPTY_ARRAY) : hookRows;

  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal;

  useEffect(() => {
    if (!isLoading && searchValue) {
      if (rows.length === 1 && String(rows[0].id) === '1') {
        const kw = searchValue.toLowerCase();
        const row = rows[0];

        const companyName = getCompanyLabel(row).toLowerCase();
        const webTitle = (row?.web_attribution?.title || '').toLowerCase();
        const loginTitle = (row?.login?.title || '').toLowerCase();
        const headerColor = (row?.header?.color || '').toLowerCase();
        const customerPoint = (row?.labels?.customer_point || '').toLowerCase();
        const pool = (row?.labels?.pool || '').toLowerCase();
        const geofence = (row?.labels?.geofence || '').toLowerCase();

        const isMatch =
          companyName.includes(kw) ||
          webTitle.includes(kw) ||
          loginTitle.includes(kw) ||
          headerColor.includes(kw) ||
          customerPoint.includes(kw) ||
          pool.includes(kw) ||
          geofence.includes(kw);

        if (!isMatch) {
          setShowFallbackWarning(true);
          if (searchValue !== lastNotifiedSearch) {
            notify({
              message: t(
                'appSetting.noExactMatchFound',
                'No exact matches found for your keyword. Displaying the default configuration as Administrator.'
              ),
              severity: 'info'
            });
            setLastNotifiedSearch(searchValue);
          }
        } else {
          setShowFallbackWarning(false);
          setLastNotifiedSearch(searchValue);
        }
      } else {
        setShowFallbackWarning(false);
        setLastNotifiedSearch(searchValue);
      }
    } else if (!searchValue) {
      setShowFallbackWarning(false);
      setLastNotifiedSearch('');
    }
  }, [isLoading, searchValue, rows, lastNotifiedSearch, notify, getCompanyLabel, t]);

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        const columns = [];
        if (searchValue) {
          searchFieldOptions.forEach((opt) => {
            if (opt.value !== 'all') {
              const colName = opt.value === 'company' ? 'name' : opt.value;
              const col = {
                name: colName,
                logic_operator: 'like',
                operator: 'OR',
                value: searchValue
              };
              if (opt.table_name) {
                col.table_name = opt.table_name;
              }
              columns.push(col);
            }
          });
        } else {
          columns.push({ logic_operator: 'like', name: 'web_title', operator: 'AND', value: '' });
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['id'], ascending: false },
          joins: [{ name: 'company', column_results: ['name'] }],
          columns: columns
        };

        const resp = await fleetApi.post('/app-setting/search', payload, { signal: controller.signal });
        const data = resp?.data ?? null;

        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalSettings(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('app-setting search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, searchFieldOptions]);

  // --- 3. HANDLERS ---
  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  // --- 4. DELETE FLOW ---
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id) => {
    setConfirmTargetId(id);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteAppSetting(confirmTargetId);
      notify({ message: t('appSetting.appSettingDeleted', 'App Setting successfully deleted'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('appSetting.failedDelete', 'Failed to delete App Setting'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
    }
  };

  const columns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1.5,
      valueGetter: (value, row) => getCompanyLabel(row || value?.row || {})
    },
    {
      field: 'web_title',
      headerName: t('appSetting.webTitle', 'Web Title'),
      flex: 1.5,
      valueGetter: (value, row) => (row || value?.row)?.web_attribution?.title || '-'
    },
    {
      field: 'login_title',
      headerName: t('appSetting.loginTitle', 'Login Title'),
      flex: 1.5,
      valueGetter: (value, row) => (row || value?.row)?.login?.title || (row || value?.row)?.web_attribution?.title || '-'
    },
    {
      field: 'header_color',
      headerName: t('appSetting.headerTitle', 'Header Color'),
      flex: 1,
      valueGetter: (value, row) => (row || value?.row)?.header?.color || '-',
      renderCell: (params) => {
        const color = params.row.header?.color;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '16px', height: '16px', borderRadius: '4px', backgroundColor: color || '#ccc' }}></div>
            {color || '-'}
          </div>
        );
      }
    },
    {
      field: 'customer_point_label',
      headerName: t('appSetting.jettyLabel', 'Jetty Label'),
      flex: 1.5,
      valueGetter: (value, row) => (row || value?.row)?.labels?.customer_point || '-'
    },
    {
      field: 'pool_label',
      headerName: t('appSetting.pitLabel', 'PIT Label'),
      flex: 1,
      valueGetter: (value, row) => (row || value?.row)?.labels?.pool || '-'
    },
    {
      field: 'geofence_label',
      headerName: t('appSetting.geofenceLabel', 'Geofence Label'),
      flex: 1,
      valueGetter: (value, row) => (row || value?.row)?.labels?.geofence || '-'
    },
    {
      field: 'is_default_header_logo',
      headerName: t('appSetting.defaultHeaderLogo', 'Default Header Logo'),
      flex: 1,
      valueGetter: (value, row) => ((row || value?.row)?.is_default_header_logo ? 'Yes' : 'No')
    },
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => {
        const isDefault = String(params.row.id) === '1';
        return <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={handleDelete} disableDelete={isDefault} mode="inline" />;
      }
    }
  ];

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;

      const fetchColumns = [];

      if (searchField !== 'all' && searchRaw) {
        const opt = searchFieldOptions.find((o) => o.value === searchField);
        if (opt) {
          const colName = opt.value === 'company' ? 'name' : opt.value;
          const searchCol = {
            name: colName,
            logic_operator: 'like',
            operator: 'AND',
            value: searchRaw
          };
          if (opt.table_name) {
            searchCol.table_name = opt.table_name;
          }
          fetchColumns.push(searchCol);
        }
      } else if (searchField === 'all' && searchRaw) {
        searchFieldOptions.forEach((opt) => {
          if (opt.value !== 'all') {
            const colName = opt.value === 'company' ? 'name' : opt.value;
            const col = {
              name: colName,
              logic_operator: 'like',
              operator: 'OR',
              value: searchRaw
            };
            if (opt.table_name) {
              col.table_name = opt.table_name;
            }
            fetchColumns.push(col);
          }
        });
      }

      if (fetchColumns.length === 0) {
        fetchColumns.push({ logic_operator: 'like', name: 'web_title', operator: 'AND', value: '' });
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['id'], ascending: false },
        joins: [{ name: 'company', column_results: ['name'] }],
        columns: fetchColumns
      };

      const resp = await fleetApi.post('/app-setting/search', payload);
      const data = resp?.data ?? null;
      const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);

      // Map the nested objects so Papa.unparse can export them properly in CSV
      const flattenedRows = fetchedRows.map((row) => ({
        ...row,
        company: getCompanyLabel(row),
        web_title: row?.web_attribution?.title || '-',
        login_title: row?.login?.title || '-',
        header_color: row?.header?.color || '-',
        customer_point_label: row?.labels?.customer_point || '-',
        pool_label: row?.labels?.pool || '-',
        geofence_label: row?.labels?.geofence || '-',
        is_default_header_logo: row?.is_default_header_logo ? 'Yes' : 'No'
      }));

      return flattenedRows;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  return (
    <div>
      <Box>
        <Collapse in={showFallbackWarning}>
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="inherit" />}
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              alignItems: 'center'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('appSetting.noExactMatch', 'No exact matches found for')} <strong>"{searchValue}"</strong>.{' '}
              {t('appSetting.defaultConfig', 'Displaying the default configuration as Administrator.')}
            </Typography>
          </Alert>
        </Collapse>
      </Box>

      <DataTable
        title="App Settings"
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('appSetting.searchAppSetting', 'Search App Settings')}
        hideCompanyFilter={true}
        onFetchAllForExport={handleFetchAllForExport}
      />

      {openDialog && (
        <AppSettingAddEdit
          open={openDialog}
          id={editingId}
          onClose={handleDialogClose}
          onSuccess={() => setPaginationModel((p) => ({ ...p }))}
        />
      )}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
      />
    </div>
  );
}

export default function AppSettingPage() {
  return <AppSettingPageContent />;
}
